import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { API } from '../../../config';
import { toast } from 'react-hot-toast';
import { PlusIcon, TrashIcon, ArrowLeftIcon, PhotoIcon } from '@heroicons/react/24/outline';

// -----------------------------------------------------------------------------
// Helper: Single Page Editor
// -----------------------------------------------------------------------------
const PageEditor = ({ page, index, onChange, onRemove }) => {
    const [overlayFile, setOverlayFile] = useState(null);

    const handleOverlayUpload = async (e) => {
        const file = e.target.files[0];
        if (!file) return;

        // Simulate upload - In real app, upload to S3 here and get URL
        // For now we assume the admin manually enters the URL or we use local preview
        // We will implement a quick S3 upload helper for this

        // Create form data for upload
        const formData = new FormData();
        formData.append('file', file);
        formData.append('folder', 'overlays');

        try {
            const token = localStorage.getItem('token');
            const res = await axios.post(`${API}/upload-image`, formData, {
                headers: {
                    'Content-Type': 'multipart/form-data',
                    Authorization: `Bearer ${token}`
                }
            });

            // Update the page config with the new overlay URL
            const newLayers = [...page.layers];
            // Find existing overlay or add new
            const overlayIndex = newLayers.findIndex(l => l.type === 'overlay');

            const overlayLayer = {
                type: 'overlay',
                src: res.data.image_url,
                z_index: 20
            };

            if (overlayIndex >= 0) {
                newLayers[overlayIndex] = overlayLayer;
            } else {
                newLayers.push(overlayLayer);
            }

            onChange({ ...page, layers: newLayers });
            toast.success("Overlay uploaded");

        } catch (err) {
            console.error(err);
            toast.error("Upload failed");
        }
    };

    const updateZone = (key, value) => {
        // Find zone layer
        const newLayers = [...page.layers];
        const zoneIndex = newLayers.findIndex(l => l.type === 'zone');

        // Default zone if not exists
        let zoneLayer = zoneIndex >= 0 ? { ...newLayers[zoneIndex] } : { type: 'zone', id: 'main', x: 50, y: 50, width: 200, height: 200, z_index: 10 };

        zoneLayer[key] = parseInt(value, 10);

        if (zoneIndex >= 0) {
            newLayers[zoneIndex] = zoneLayer;
        } else {
            newLayers.unshift(zoneLayer); // Zones usually go under overlays
        }

        onChange({ ...page, layers: newLayers });
    };

    const getZone = () => page.layers.find(l => l.type === 'zone') || { x: 50, y: 50, width: 200, height: 200 };
    const getOverlay = () => page.layers.find(l => l.type === 'overlay');

    const zone = getZone();
    const overlay = getOverlay();

    return (
        <div className="bg-white p-6 rounded-xl border border-gray-200 shadow-sm mb-4">
            <div className="flex justify-between items-center mb-4">
                <h3 className="font-bold text-gray-800">Page {index + 1}: {page.name}</h3>
                <button onClick={onRemove} className="text-red-500 hover:text-red-700 p-2">
                    <TrashIcon className="h-5 w-5" />
                </button>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* Layer 1: Overlay */}
                <div className="space-y-3">
                    <label className="block text-sm font-medium text-gray-700">Overlay Image (PNG with Transparency)</label>
                    <div className="flex items-center gap-4">
                        {overlay && (
                            <div
                                className="w-20 h-20 rounded-lg overflow-hidden border border-gray-200 relative"
                                style={{
                                    backgroundImage: 'repeating-conic-gradient(#e5e7eb 0% 25%, #ffffff 0% 50%)',
                                    backgroundSize: '10px 10px'
                                }}
                            >
                                <img src={overlay.src} alt="Overlay" className="w-full h-full object-contain" />
                            </div>
                        )}
                        <label className="cursor-pointer bg-gray-50 border border-gray-300 text-gray-700 px-4 py-2 rounded-lg hover:bg-gray-100 transition-colors text-sm font-medium flex items-center gap-2">
                            <PhotoIcon className="h-4 w-4" />
                            Upload Overlay
                            <input type="file" accept="image/png" className="hidden" onChange={handleOverlayUpload} />
                        </label>
                    </div>
                </div>

                {/* Layer 2: Drop Zone Config */}
                <div className="space-y-3">
                    <label className="block text-sm font-medium text-gray-700">User Drop Zone (Coordinates)</label>
                    <div className="grid grid-cols-2 gap-2">
                        <div>
                            <span className="text-xs text-gray-500">X Position</span>
                            <input type="number" value={zone.x} onChange={(e) => updateZone('x', e.target.value)} className="w-full border rounded p-1 text-sm" />
                        </div>
                        <div>
                            <span className="text-xs text-gray-500">Y Position</span>
                            <input type="number" value={zone.y} onChange={(e) => updateZone('y', e.target.value)} className="w-full border rounded p-1 text-sm" />
                        </div>
                        <div>
                            <span className="text-xs text-gray-500">Width</span>
                            <input type="number" value={zone.width} onChange={(e) => updateZone('width', e.target.value)} className="w-full border rounded p-1 text-sm" />
                        </div>
                        <div>
                            <span className="text-xs text-gray-500">Height</span>
                            <input type="number" value={zone.height} onChange={(e) => updateZone('height', e.target.value)} className="w-full border rounded p-1 text-sm" />
                        </div>
                    </div>
                </div>
            </div>

            {/* Preview Mini Canvas (Visual Check) */}
            <div className="mt-4 relative overflow-hidden mx-auto rounded border border-gray-300"
                style={{
                    width: 400,
                    height: 300,
                    backgroundImage: 'repeating-conic-gradient(#e5e7eb 0% 25%, #ffffff 0% 50%)',
                    backgroundSize: '20px 20px'
                }}>
                {/* Scale down 800x600 to 400x300 (0.5 scale) */}
                <div style={{ transform: 'scale(0.5)', transformOrigin: 'top left', width: 800, height: 600, position: 'relative' }}>
                    {/* Zone Representation */}
                    <div
                        style={{
                            position: 'absolute',
                            left: zone.x,
                            top: zone.y,
                            width: zone.width,
                            height: zone.height,
                            background: 'rgba(59, 130, 246, 0.3)',
                            border: '2px dashed #3b82f6',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            color: '#1d4ed8',
                            fontWeight: 'bold'
                        }}
                    >
                        Drop Zone
                    </div>

                    {/* Overlay Representation */}
                    {overlay && (
                        <img
                            src={overlay.src}
                            alt=""
                            style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', objectFit: 'contain', pointerEvents: 'none' }}
                        />
                    )}
                </div>
            </div>
        </div>
    );
};


// -----------------------------------------------------------------------------
// Main Template Builder
// -----------------------------------------------------------------------------

const TemplateBuilder = () => {
    const { productId } = useParams();
    const navigate = useNavigate();
    const [loading, setLoading] = useState(true);
    const [pages, setPages] = useState([]);
    const [productTitle, setProductTitle] = useState('');

    useEffect(() => {
        fetchProductConfig();
    }, [productId]);

    const fetchProductConfig = async () => {
        try {
            const res = await axios.get(`${API}/products/${productId}`);
            setProductTitle(res.data.title);

            // Allow existing config or start fresh
            if (res.data.customization_config?.pages) {
                setPages(res.data.customization_config.pages);
            } else {
                // Initial Default Page
                setPages([{
                    id: 'page-1',
                    name: 'Page 1',
                    layers: []
                }]);
            }
        } catch (err) {
            console.error(err);
            toast.error("Failed to load product");
        } finally {
            setLoading(false);
        }
    };

    const handleSave = async () => {
        try {
            const token = localStorage.getItem('token');
            const config = {
                pages: pages
            };

            await axios.put(`${API}/products/${productId}/template`, config, {
                headers: { Authorization: `Bearer ${token}` }
            });
            toast.success("Template saved successfully!");
        } catch (err) {
            console.error(err);
            toast.error("Failed to save template");
        }
    };

    const addPage = () => {
        setPages(prev => [...prev, {
            id: `page-${prev.length + 1}`,
            name: `Page ${prev.length + 1}`,
            layers: []
        }]);
    };

    const updatePage = (idx, newPageConfig) => {
        setPages(prev => {
            const copy = [...prev];
            copy[idx] = newPageConfig;
            return copy;
        });
    };

    const removePage = (idx) => {
        if (window.confirm("Remove this page?")) {
            setPages(prev => prev.filter((_, i) => i !== idx));
        }
    };

    if (loading) return <div className="p-8">Loading...</div>;

    return (
        <div className="min-h-screen bg-gray-50 pb-20">
            {/* Header */}
            <div className="bg-white border-b border-gray-200 sticky top-0 z-10 px-8 py-4 flex items-center justify-between shadow-sm">
                <div className="flex items-center gap-4">
                    <button onClick={() => navigate('/admin')} className="text-gray-500 hover:text-gray-900">
                        <ArrowLeftIcon className="h-6 w-6" />
                    </button>
                    <div>
                        <h1 className="text-xl font-bold text-gray-900">Template Builder: {productTitle}</h1>
                        <p className="text-sm text-gray-500">Configure drop zones and overlays</p>
                    </div>
                </div>
                <button
                    onClick={handleSave}
                    className="bg-[#111114] text-white px-6 py-2.5 rounded-lg font-bold hover:bg-gray-800 transition-colors shadow-lg"
                >
                    Save Template
                </button>
            </div>

            {/* Content */}
            <div className="max-w-4xl mx-auto mt-8 px-4">
                {pages.map((page, idx) => (
                    <PageEditor
                        key={idx}
                        index={idx}
                        page={page}
                        onChange={(newPage) => updatePage(idx, newPage)}
                        onRemove={() => removePage(idx)}
                    />
                ))}

                <button
                    onClick={addPage}
                    className="w-full py-4 border-2 border-dashed border-gray-300 rounded-xl text-gray-500 font-bold hover:border-gray-400 hover:bg-gray-100 transition-all flex items-center justify-center gap-2"
                >
                    <PlusIcon className="h-6 w-6" />
                    Add Another Page
                </button>
            </div>
        </div>
    );
};

export default TemplateBuilder;
